import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { createApiKey, listApiKeys, revokeApiKey } from '../../api/apiKeys';
import { logEvent } from '../../utils/analytics';

function KeyRow({ k, onRevoke }) {
  const [confirming, setConfirming] = useState(false);
  const [revoking,   setRevoking]   = useState(false);

  const handleRevoke = async () => {
    setRevoking(true);
    await onRevoke(k._id);
    setRevoking(false);
  };

  const created = new Date(k.createdAt).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  });

  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-white/8 bg-white/[0.03] px-5 py-4">
      <div className="flex items-center gap-4 min-w-0">
        <span className="shrink-0 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
          <span className="material-symbols-outlined text-[18px] text-primary">key</span>
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{k.name}</p>
          <p className="mt-0.5 font-mono text-xs text-slate-400">
            {k.prefix}
            <span className="text-slate-600">{'•'.repeat(24)}</span>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-6 shrink-0">
        <div className="text-right">
          <p className="text-xs text-slate-500">Created</p>
          <p className="text-xs font-semibold text-slate-300">{created}</p>
        </div>

        {confirming ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Revoke?</span>
            <button
              onClick={handleRevoke}
              disabled={revoking}
              className="rounded-lg bg-red-500/20 px-3 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/30 transition disabled:opacity-50"
            >
              {revoking ? 'Revoking…' : 'Yes, revoke'}
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="rounded-lg border border-white/10 px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-white transition"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            className="rounded-lg border border-red-500/20 px-3 py-1.5 text-xs font-semibold text-red-400 hover:border-red-500/40 hover:bg-red-500/10 transition"
          >
            Revoke
          </button>
        )}
      </div>
    </div>
  );
}

function NewKeyReveal({ keyValue, onDone }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(keyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5">
      <div className="mb-3 flex items-center gap-2">
        <span className="material-symbols-outlined text-[18px] text-emerald-400" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
        <p className="text-sm font-bold text-emerald-400">API key created — copy it now</p>
      </div>
      <p className="mb-3 text-xs text-slate-400">
        This key will <span className="font-bold text-white">never be shown again</span>. Store it securely (e.g. in a secrets manager or .env file).
      </p>
      <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-slate-900 px-4 py-3">
        <code className="flex-1 break-all font-mono text-sm text-emerald-300">{keyValue}</code>
        <button
          onClick={copy}
          className={`shrink-0 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
            copied ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white hover:bg-white/20'
          }`}
        >
          <span className="material-symbols-outlined text-[14px]">{copied ? 'check' : 'content_copy'}</span>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <button
        onClick={onDone}
        className="mt-4 text-xs text-slate-500 hover:text-white transition"
      >
        I've copied it — dismiss
      </button>
    </div>
  );
}

export function ApiKeysTab() {
  const { user } = useAuth();
  const token  = user?.token;
  const userId = user?.id;
  const [keys,        setKeys]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [creating,    setCreating]    = useState(false);
  const [showForm,    setShowForm]    = useState(false);
  const [keyName,     setKeyName]     = useState('');
  const [newKey,      setNewKey]      = useState(null);
  const [error,       setError]       = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await listApiKeys(token, userId);
      setKeys(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!keyName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const data = await createApiKey(keyName.trim(), token, userId);
      setNewKey(data.key);
      setKeyName('');
      setShowForm(false);
      logEvent({ name: 'api_key_created', type: 'api', userEmail: user?.email || '', meta: { keyName: keyName.trim() } });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await revokeApiKey(id, token, userId);
      setKeys((prev) => prev.filter((k) => k._id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-black text-white">API Keys</h1>
          <p className="mt-1 text-sm text-slate-400">
            Use these keys to authenticate calls to the Feuteuristix API. Treat them like passwords — never commit them to source control.
          </p>
        </div>
        {!showForm && !newKey && (
          <button
            onClick={() => setShowForm(true)}
            className="shrink-0 flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-4 py-2.5 text-sm font-bold text-white hover:brightness-110 transition"
          >
            <span className="material-symbols-outlined text-[16px]">add</span>
            New key
          </button>
        )}
      </div>

      {/* Newly-created key reveal */}
      {newKey && (
        <div className="mb-6">
          <NewKeyReveal keyValue={newKey} onDone={() => setNewKey(null)} />
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleCreate} className="mb-6 rounded-xl border border-white/10 bg-white/[0.04] p-5">
          <p className="mb-4 text-sm font-bold text-white">Create a new API key</p>
          <div className="flex items-center gap-3">
            <input
              autoFocus
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder="Key label (e.g. Production, Staging)"
              className="flex-1 h-10 rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white placeholder:text-slate-600 focus:border-primary/60 focus:outline-none"
            />
            <button
              type="submit"
              disabled={creating || !keyName.trim()}
              className="shrink-0 h-10 rounded-lg bg-primary px-4 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setKeyName(''); }}
              className="shrink-0 h-10 rounded-lg border border-white/10 px-4 text-sm font-semibold text-slate-400 hover:text-white transition"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Key list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl border border-white/5 bg-white/[0.02] animate-pulse" />
          ))}
        </div>
      ) : keys.length === 0 && !newKey ? (
        <div className="rounded-xl border border-white/8 bg-white/[0.02] px-6 py-12 text-center">
          <span className="material-symbols-outlined text-[40px] text-slate-600">key_off</span>
          <p className="mt-3 text-sm font-semibold text-slate-400">No API keys yet</p>
          <p className="mt-1 text-xs text-slate-600">Create your first key to start calling the API.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map((k) => (
            <KeyRow key={k._id} k={k} onRevoke={handleRevoke} />
          ))}
        </div>
      )}

      {/* Usage note */}
      <div className="mt-8 rounded-xl border border-white/8 bg-white/[0.02] px-5 py-4">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Using your key</p>
        <code className="block font-mono text-xs text-slate-300 leading-relaxed">
          x-api-key: ftx_live_<span className="text-slate-600">••••••••••••</span>
        </code>
        <p className="mt-2 text-xs text-slate-600">
          Pass your key in the <span className="text-slate-400">x-api-key</span> header on every API request.
          See the <a href="/developers" className="text-primary hover:underline">Developers docs</a> for the full reference.
        </p>
      </div>
    </div>
  );
}
